// =====================================================================
//  HRMS ADMIN CONSOLE, APPLICATION SHELL
// ---------------------------------------------------------------------
//  The operator console for an organization's administrators. It is a
//  separate application from the employee portal, not a portal with a
//  few extra menu items:
//
//    · A graphite command rail, collapsible to icons, grouped by the job
//      being done, with live counts on the queues that need attention.
//    · Real routing. Every screen has a URL (#/people, #/leave), so the
//      back button, refresh and shared links all work, the portal's
//      string-switch navigation supports none of that.
//    · A ⌘K command palette for navigation and actions.
//    · One place to see everything waiting on a decision.
//
//  There is deliberately no route into the employee portal from here. The
//  two are separate applications for separate jobs, and an account menu
//  that offered to swap between them made the boundary ambiguous in both
//  directions. A session belongs to one or the other, decided by role at
//  sign-in (see HRMSApp).
// =====================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  Check,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  Menu as MenuIcon,
  RefreshCw,
  Search,
  Settings,
  UserCircle,
} from 'lucide-react';

import backendApi from '../api/backendApi';
import './admin.css';
import { DEFAULT_ROUTE, NAV_INDEX, countFor, isValidRoute, navGroupsForRole } from './nav';
import {
  Avatar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  cx,
  usePersistentState,
  useEscape,
  useOutsideClick,
} from './ui';
import { timeAgo } from './format';
import CommandPalette from './CommandPalette';

import OverviewPage from './pages/OverviewPage';
import PeoplePage from './pages/PeoplePage';
import AttendancePage from './pages/AttendancePage';
import LeavePage from './pages/LeavePage';
import ApprovalsPage from './pages/ApprovalsPage';
import LeaveSetupPage from './pages/LeaveSetupPage';
import ShiftsPage from './pages/ShiftsPage';
import PayrollPage from './pages/PayrollPage';
import PerformancePage from './pages/PerformancePage';

import HelpdeskPage from './pages/HelpdeskPage';
import DocumentsPage from './pages/DocumentsPage';
import ReportsPage from './pages/ReportsPage';
import AccessControlPage from './pages/AccessControlPage';
import OrganizationPage from './pages/OrganizationPage';
import ActivityLogPage from './pages/ActivityLogPage';

import OnboardingInvitesPage from './pages/OnboardingInvitesPage';
import OnboardingSubmissionsPage from './pages/OnboardingSubmissionsPage';
import OnboardingAnalyticsPage from './pages/OnboardingAnalyticsPage';

/* =====================================================================
   HASH ROUTING
   ---------------------------------------------------------------------
   The console lives under the app's `/hrms/*` wildcard route, which is
   not itself route-driven. Rather than restructure the host router, the
   console keys off `location.hash`, that still gives real history
   entries, working back/forward, and shareable deep links.
   ================================================================== */

const readHash = () => {
  const raw = String(window.location.hash || '').replace(/^#\/?/, '');
  const [key] = raw.split('?');
  return isValidRoute(key) ? key : DEFAULT_ROUTE;
};

function useHashRoute() {
  const [route, setRoute] = useState(readHash);

  useEffect(() => {
    const onHashChange = () => setRoute(readHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const navigate = useCallback((key) => {
    if (!isValidRoute(key)) return;
    if (readHash() === key) return;
    window.location.hash = `#/${key}`;
  }, []);

  return [route, navigate];
}

/* =====================================================================
   SHELL
   ================================================================== */

export default function AdminApp({ onLogout, userName }) {
  const queryClient = useQueryClient();
  const [route, navigate] = useHashRoute();
  const [collapsed, setCollapsed] = usePersistentState('railCollapsed', false);
  const [mobileRailOpen, setMobileRailOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const storedUser = backendApi.auth.getStoredUser();
  const role = storedUser?.role || 'admin';
  const displayName = userName || storedUser?.full_name || storedUser?.name || 'Administrator';
  const email = storedUser?.email;
  const roleLabel = role === 'hr' ? 'HR' : role.charAt(0).toUpperCase() + role.slice(1);

  /* ---- permissions -------------------------------------------------- */
  // Resolved server-side from the role and shipped by /auth/me, so the
  // console and the API agree on who may do what. Pages previously each
  // re-derived this with their own hard-coded role array
  // (`['admin','hr'].includes(role)`), which meant every grant was written
  // twice and the two copies drifted, managers hold leave.approve but were
  // shown a read-only leave screen.
  const { data: permissions } = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: async () => {
      await backendApi.auth.me();
      return backendApi.auth.getPermissions();
    },
    staleTime: 5 * 60_000,
  });

  // Until the query resolves, `can` answers false for everything. That means
  // a privileged control appears a moment late rather than flashing up and
  // then vanishing, which is the less confusing of the two failure modes.
  const can = useCallback(
    (permissionKey) => Boolean(permissions?.includes(permissionKey)),
    [permissions]
  );

  /* ---- live queue counts drive the rail badges ---------------------- */
  const { data: counts } = useQuery({
    queryKey: ['admin', 'approval-counts'],
    queryFn: () => backendApi.admin.approvalCounts(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const groups = useMemo(() => navGroupsForRole(role), [role]);
  const current = NAV_INDEX[route] || NAV_INDEX[DEFAULT_ROUTE];

  /* ---- notifications ----------------------------------------------- */
  const { data: notifications = [] } = useQuery({
    queryKey: ['admin', 'notifications'],
    queryFn: () => backendApi.notifications.list({ limit: 12 }),
    refetchInterval: 60_000,
  });

  const notificationList = Array.isArray(notifications)
    ? notifications
    : notifications?.notifications || [];
  const unread = notificationList.filter((n) => !n.read).length;

  /* ---- global shortcuts -------------------------------------------- */
  useEffect(() => {
    const onKey = (event) => {
      const meta = event.metaKey || event.ctrlKey;
      if (meta && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((prev) => !prev);
        return;
      }
      // "/" focuses search, but not while the user is typing in a field.
      const tag = String(event.target?.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || tag === 'select' || event.target?.isContentEditable;
      if (event.key === '/' && !typing) {
        event.preventDefault();
        setPaletteOpen(true);
      }
      if (meta && event.key === '\\') {
        event.preventDefault();
        setCollapsed((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setCollapsed]);

  // Close the mobile rail whenever navigation happens.
  useEffect(() => setMobileRailOpen(false), [route]);

  const refreshAll = useCallback(() => {
    queryClient.invalidateQueries();
  }, [queryClient]);

  const go = useCallback(
    (key) => {
      navigate(key);
      // Jump the scroll container back to the top on navigation, the way a
      // real page load would.
      requestAnimationFrame(() => {
        document.querySelector('.ac-content')?.scrollTo({ top: 0 });
      });
    },
    [navigate]
  );

  /* ---- page router -------------------------------------------------- */
  const renderPage = () => {
    // `can` is the permission predicate; `role` is still passed for labels
    // and copy. New gating should use `can`, not `role`.
    const shared = { role, can, navigate: go, currentUser: storedUser };
    switch (route) {
      case 'people': return <PeoplePage {...shared} />;
      case 'attendance': return <AttendancePage {...shared} />;
      case 'leave': return <LeavePage {...shared} />;
      case 'approvals': return <ApprovalsPage {...shared} />;
      case 'leave-setup': return <LeaveSetupPage {...shared} />;
      case 'shifts': return <ShiftsPage {...shared} />;
      case 'payroll': return <PayrollPage {...shared} />;
      case 'performance': return <PerformancePage {...shared} />;

      case 'helpdesk': return <HelpdeskPage {...shared} />;
      case 'documents': return <DocumentsPage {...shared} />;
      case 'reports': return <ReportsPage {...shared} />;
      case 'access': return <AccessControlPage {...shared} />;
      case 'organization': return <OrganizationPage {...shared} />;
      case 'activity': return <ActivityLogPage {...shared} />;

      case 'onboarding-invites': return <OnboardingInvitesPage {...shared} />;
      case 'onboarding-submissions': return <OnboardingSubmissionsPage {...shared} />;
      case 'onboarding-analytics': return <OnboardingAnalyticsPage {...shared} />;

      case 'overview':
      default:
        return <OverviewPage {...shared} counts={counts} />;
    }
  };

  return (
    <div className="ac-root">
      {/* ============ COMMAND RAIL ============ */}
      <nav
        className={cx('ac-rail', collapsed && 'ac-rail--collapsed', mobileRailOpen && 'ac-rail--open')}
        aria-label="Admin navigation"
      >
        <div
          className="ac-rail-brand"
          onClick={() => go(DEFAULT_ROUTE)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter') go(DEFAULT_ROUTE);
          }}
        >
          <img src="/logo 2.jpg" alt="" className="ac-rail-mark" />
          {!collapsed ? (
            <div style={{ minWidth: 0 }}>
              <div className="ac-rail-wordmark">WEBIDEAL</div>
              <div className="ac-rail-sub">HRMS Admin</div>
            </div>
          ) : null}
        </div>

        <div className="ac-rail-nav">
          {groups.map((group) => (
            <div className="ac-rail-group" key={group.label}>
              <div className="ac-rail-group-label">{group.label}</div>
              {group.items.map((item) => {
                const active = route === item.key;
                const badge = countFor(item, counts);
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={cx('ac-nav', active && 'ac-nav--active')}
                    onClick={() => go(item.key)}
                    aria-current={active ? 'page' : undefined}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon className="ac-nav-icon" strokeWidth={1.75} />
                    <span className="ac-nav-text">{item.label}</span>
                    {badge > 0 ? (
                      <span className="ac-nav-count" title={`${badge} awaiting action`}>
                        {badge > 99 ? '99+' : badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="ac-rail-foot">
          <Menu
            align="left"
            placement="top"
            width={222}
            trigger={({ toggle }) => (
              <button type="button" className="ac-rail-user" onClick={toggle}>
                <Avatar name={displayName} email={email} dark />
                {!collapsed ? (
                  <div className="ac-rail-user-meta" style={{ minWidth: 0, flex: '1 1 auto' }}>
                    <div className="ac-rail-user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {displayName}
                    </div>
                    <div className="ac-rail-user-role">{roleLabel}</div>
                  </div>
                ) : null}
              </button>
            )}
          >
            <MenuLabel>{email || displayName}</MenuLabel>
            <MenuItem icon={UserCircle} onClick={() => go('organization')}>
              Organization settings
            </MenuItem>
            <MenuSeparator />
            <MenuItem icon={LogOut} danger onClick={onLogout}>
              Sign out
            </MenuItem>
          </Menu>

          {!collapsed ? (
            <button
              type="button"
              className="ac-nav"
              style={{ marginTop: 2 }}
              onClick={() => setCollapsed(true)}
              title="Collapse sidebar (Ctrl+\)"
            >
              <ChevronsLeft className="ac-nav-icon" strokeWidth={1.75} />
              <span className="ac-nav-text">Collapse</span>
            </button>
          ) : (
            <button
              type="button"
              className="ac-nav"
              style={{ marginTop: 2 }}
              onClick={() => setCollapsed(false)}
              title="Expand sidebar (Ctrl+\)"
            >
              <ChevronsRight className="ac-nav-icon" strokeWidth={1.75} />
            </button>
          )}
        </div>
      </nav>

      {/* Scrim behind the rail on small screens. */}
      {mobileRailOpen ? (
        <div className="ac-overlay" style={{ zIndex: 54 }} onClick={() => setMobileRailOpen(false)} aria-hidden="true" />
      ) : null}

      {/* ============ MAIN ============ */}
      <div className="ac-main">
        <header className="ac-topbar">
          <IconButton
            icon={MenuIcon}
            label="Open navigation"
            onClick={() => setMobileRailOpen(true)}
            className="ac-rail-toggle"
          />

          <div className="ac-crumbs">
            <button type="button" className="ac-crumb" onClick={() => go(DEFAULT_ROUTE)}>
              {storedUser?.tenant_name || 'Organization'}
            </button>
            <span className="ac-crumb-sep">/</span>
            {current.group && current.key !== DEFAULT_ROUTE ? (
              <>
                <span className="ac-crumb" style={{ cursor: 'default' }}>{current.group}</span>
                <span className="ac-crumb-sep">/</span>
              </>
            ) : null}
            <span className="ac-crumb ac-crumb--current">{current.title}</span>
          </div>

          <div style={{ flex: '1 1 auto' }} />

          <button type="button" className="ac-omni" onClick={() => setPaletteOpen(true)}>
            <Search size={13} strokeWidth={2} />
            <span className="ac-omni-text">Search or jump to…</span>
            <span className="ac-kbd">Ctrl K</span>
          </button>

          <IconButton icon={RefreshCw} label="Refresh data" onClick={refreshAll} />

          <NotificationBell
            items={notificationList}
            unread={unread}
            onOpenAll={() => go('activity')}
          />

          <Menu
            align="right"
            width={210}
            trigger={({ toggle }) => (
              <button
                type="button"
                onClick={toggle}
                className="ac-iconbtn"
                style={{ width: 'auto', padding: '0 4px', gap: 6 }}
                aria-label="Account menu"
              >
                <Avatar name={displayName} email={email} size="sm" />
              </button>
            )}
          >
            <MenuLabel>Signed in as {roleLabel}</MenuLabel>
            <MenuItem icon={Settings} onClick={() => go('organization')}>
              Settings
            </MenuItem>
            <MenuSeparator />
            <MenuItem icon={LogOut} danger onClick={onLogout}>
              Sign out
            </MenuItem>
          </Menu>
        </header>

        <main className="ac-content">{renderPage()}</main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={go}
        counts={counts}
        role={role}
      />
    </div>
  );
}

/* =====================================================================
   NOTIFICATION BELL
   ================================================================== */

function NotificationBell({ items, unread, onOpenAll }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const queryClient = useQueryClient();
  const close = useCallback(() => setOpen(false), []);
  useOutsideClick(ref, close, open);
  useEscape(close, open);

  const markAllRead = async () => {
    try {
      await backendApi.notifications.markAllRead();
      queryClient.invalidateQueries({ queryKey: ['admin', 'notifications'] });
    } catch {
      /* non-critical: the badge will correct itself on the next poll */
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="ac-iconbtn"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      >
        <Bell size={16} strokeWidth={1.9} />
        {unread > 0 ? <span className="ac-dot">{unread > 9 ? '9+' : unread}</span> : null}
      </button>

      {open ? (
        <div className="ac-menu ac-menu--right ac-menu--below" style={{ width: 320, padding: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 12px',
              borderBottom: '1px solid var(--ac-line-soft)',
            }}
          >
            <span className="ac-eyebrow">Notifications</span>
            {unread > 0 ? (
              <Button size="sm" variant="ghost" icon={Check} onClick={markAllRead}>
                Mark all read
              </Button>
            ) : null}
          </div>

          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {items.length === 0 ? (
              <div style={{ padding: '26px 14px', textAlign: 'center', fontSize: 12, color: 'var(--ac-faint)' }}>
                Nothing new
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.id}
                  style={{
                    padding: '10px 12px',
                    borderBottom: '1px solid var(--ac-line-soft)',
                    background: item.read ? 'transparent' : 'var(--ac-accent-soft)',
                  }}
                >
                  <div style={{ fontSize: 12.5, color: 'var(--ac-ink)', fontWeight: 550 }}>
                    {item.title || item.message}
                  </div>
                  {item.title && item.message ? (
                    <div style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 2 }}>{item.message}</div>
                  ) : null}
                  <div style={{ fontSize: 10.5, color: 'var(--ac-faint)', marginTop: 3 }}>
                    {timeAgo(item.created_at)}
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ padding: 6, borderTop: '1px solid var(--ac-line-soft)' }}>
            <Button
              size="sm"
              variant="ghost"
              block
              onClick={() => {
                close();
                onOpenAll();
              }}
            >
              View activity log
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
